import React, { useState, useRef, createElement } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { toPng } from 'html-to-image';
import { XIcon, DownloadIcon } from 'lucide-react';
import { MermaidDiagram } from './MermaidDiagram';
import { TranslationKey } from '../i18n/translations';
interface ExportModalProps {
  markdown: string;
  onClose: () => void;
  isDark?: boolean;
  t: (key: TranslationKey) => string;
}
type ExportSize = 'wechat';
type ColorTheme = 'nordic' | 'warm' | 'cool' | 'dark';
export function ExportModal({
  markdown,
  onClose,
  isDark = false,
  t
}: ExportModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedSize, setSelectedSize] = useState<ExportSize>('wechat');
  const [selectedTheme, setSelectedTheme] = useState<ColorTheme>('nordic');
  const sizeOptions = {
    wechat: {
      width: 375,
      labelKey: 'export.sizeWechat' as TranslationKey
    }
  };
  const colorThemes = {
    nordic: {
      labelKey: 'export.themeNordic' as TranslationKey,
      background: 'linear-gradient(to bottom, #fafafa 0%, #f5f5f5 100%)',
      textColor: '#4a5568',
      headingColor: '#2c3e50',
      isDark: false
    },
    warm: {
      labelKey: 'export.themeWarm' as TranslationKey,
      background: 'linear-gradient(to bottom, #fef9f3 0%, #fcefd9 100%)',
      textColor: '#5a4632',
      headingColor: '#3d2817',
      isDark: false
    },
    cool: {
      labelKey: 'export.themeCool' as TranslationKey,
      background: 'linear-gradient(to bottom, #f0f7ff 0%, #e0f0ff 100%)',
      textColor: '#2d4a5f',
      headingColor: '#1a3547',
      isDark: false
    },
    dark: {
      labelKey: 'export.themeDark' as TranslationKey,
      background: 'linear-gradient(to bottom, #1a1d23 0%, #242830 100%)',
      textColor: '#e8ecf0',
      headingColor: '#f5f7f9',
      isDark: true
    }
  };
  const currentTheme = colorThemes[selectedTheme];
  const currentSize = sizeOptions[selectedSize];
  const handleExport = async () => {
    if (!contentRef.current) return;
    setIsExporting(true);
    try {
      // 等待更长时间确保 Mermaid 图表完全渲染
      await new Promise(resolve => setTimeout(resolve, 2000));
      const dataUrl = await toPng(contentRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: currentTheme.isDark ? '#1a1d23' : '#fafafa',
        fontEmbedCSS: true,
        skipFonts: false
      });
      const link = document.createElement('a');
      link.download = `markdown-note-${selectedSize}-${selectedTheme}-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('导出失败:', error);
      alert(t('export.failed'));
    } finally {
      setIsExporting(false);
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4 backdrop-blur-sm" data-id="element-28">
      <div className={`rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col ${isDark ? 'bg-[#242830]' : 'bg-white'}`} data-id="element-29">
        <div className="flex items-center justify-between px-8 py-6" data-id="element-30">
          <h2 className={`text-xl font-light tracking-wide ${isDark ? 'text-[#f5f7f9]' : 'text-[#2c3e50]'}`} data-id="element-31">
            {t('export.title')}
          </h2>
          <button onClick={onClose} className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-[#2d3340]' : 'hover:bg-[#f5f5f5]'}`} data-id="element-32">
            <XIcon className={`w-5 h-5 ${isDark ? 'text-[#7b92a8]' : 'text-[#5b7c8d]'}`} data-id="element-33" />
          </button>
        </div>

        <div className={`px-8 py-5 flex items-center gap-8 flex-wrap ${isDark ? 'bg-[#2d3340]' : 'bg-[#fafafa]'}`} data-id="element-34">
          <div className="flex items-center gap-4" data-id="element-35">
            <label className={`text-sm font-light tracking-wide ${isDark ? 'text-[#7b92a8]' : 'text-[#5b7c8d]'}`} data-id="element-36">
              {t('export.size')}
            </label>
            <div className="flex gap-3" data-id="element-37">
              {(Object.keys(sizeOptions) as ExportSize[]).map(size => <button key={size} onClick={() => setSelectedSize(size)} className={`px-5 py-2 text-sm rounded-full transition-all duration-200 font-light ${selectedSize === size ? isDark ? 'bg-[#7b92a8] text-white shadow-sm' : 'bg-[#5b7c8d] text-white shadow-sm' : isDark ? 'bg-[#242830] text-[#7b92a8] hover:bg-[#353b4a] border border-[#3a3f4a]' : 'bg-white text-[#5b7c8d] hover:bg-[#f5f5f5] border border-[#e8ecf0]'}`} data-id="element-38">
                  {t(sizeOptions[size].labelKey)}
                </button>)}
            </div>
          </div>

          <div className="flex items-center gap-4" data-id="element-39">
            <label className={`text-sm font-light tracking-wide ${isDark ? 'text-[#7b92a8]' : 'text-[#5b7c8d]'}`} data-id="element-40">
              {t('export.theme')}
            </label>
            <div className="flex gap-3" data-id="element-41">
              {(Object.keys(colorThemes) as ColorTheme[]).map(theme => <button key={theme} onClick={() => setSelectedTheme(theme)} className={`px-5 py-2 text-sm rounded-full transition-all duration-200 font-light ${selectedTheme === theme ? isDark ? 'bg-[#7b92a8] text-white shadow-sm' : 'bg-[#5b7c8d] text-white shadow-sm' : isDark ? 'bg-[#242830] text-[#7b92a8] hover:bg-[#353b4a] border border-[#3a3f4a]' : 'bg-white text-[#5b7c8d] hover:bg-[#f5f5f5] border border-[#e8ecf0]'}`} data-id="element-42">
                  {t(colorThemes[theme].labelKey)}
                </button>)}
            </div>
          </div>
        </div>

        <div className={`flex-1 overflow-y-auto p-8 ${isDark ? 'bg-[#1a1d23]' : 'bg-[#f7f7f5]'}`} data-id="element-43">
          <div className="flex justify-center" data-id="element-44">
            <div ref={contentRef} className="rounded-2xl shadow-sm" style={{
            width: `${currentSize.width}px`,
            background: currentTheme.background,
            paddingTop: selectedSize === 'wechat' ? '48px' : '48px',
            paddingBottom: '48px',
            paddingLeft: selectedSize === 'wechat' ? '24px' : '48px',
            paddingRight: selectedSize === 'wechat' ? '24px' : '48px'
          }} data-id="element-45">
              <div className={`prose prose-slate max-w-none ${currentTheme.isDark ? 'prose-invert' : ''}`} style={{
              color: currentTheme.textColor
            }} data-id="element-46">
                <style data-id="element-47">
                  {`
                    ${selectedSize === 'wechat' ? `
                      .prose * {
                        font-size: 16px !important;
                        line-height: 26px !important;
                        font-weight: 300 !important;
                      }
                      .prose h1 {
                        font-size: 24px !important;
                        line-height: 32px !important;
                        margin-top: 0 !important;
                        margin-bottom: 16px !important;
                        font-weight: 300 !important;
                        letter-spacing: -0.02em !important;
                      }
                      .prose h2 {
                        font-size: 20px !important;
                        line-height: 28px !important;
                        margin-top: 28px !important;
                        margin-bottom: 12px !important;
                        font-weight: 400 !important;
                        letter-spacing: -0.01em !important;
                      }
                      .prose h3 {
                        font-size: 18px !important;
                        line-height: 26px !important;
                        margin-top: 20px !important;
                        margin-bottom: 10px !important;
                        font-weight: 400 !important;
                      }
                      .prose p {
                        font-size: 16px !important;
                        line-height: 26px !important;
                        margin-top: 12px !important;
                        margin-bottom: 12px !important;
                      }
                      .prose ul, .prose ol {
                        font-size: 16px !important;
                        line-height: 26px !important;
                      }
                      .prose li {
                        font-size: 16px !important;
                        line-height: 26px !important;
                      }
                      .prose code {
                        font-size: 14px !important;
                        font-weight: 400 !important;
                      }
                      .prose pre {
                        font-size: 14px !important;
                        line-height: 22px !important;
                      }
                      /* Mermaid diagram responsive scaling for narrow export */
                      .mermaid-container {
                        padding: 0.125rem !important;
                        margin-left: -4px !important;
                        margin-right: -4px !important;
                      }
                      .mermaid-container svg {
                        width: 100% !important;
                        height: auto !important;
                        max-width: 100% !important;
                      }
                    ` : ''}
                    .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
                      color: ${currentTheme.headingColor} !important;
                    }
                    .prose strong {
                      color: ${currentTheme.headingColor} !important;
                      font-weight: 500 !important;
                    }
                  `}
                </style>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                code({
                  node,
                  inline,
                  className,
                  children,
                  ...props
                }) {
                  const match = /language-(\w+)/.exec(className || '');
                  const language = match ? match[1] : '';
                  if (!inline && language === 'mermaid') {
                    const code = String(children).replace(/\n$/, '');
                    return <MermaidDiagram code={code} isDark={currentTheme.isDark} t={t} exportMode={true} containerWidth={currentSize.width} data-id="element-49" />;
                  }
                  return !inline && match ? <SyntaxHighlighter style={currentTheme.isDark ? oneDark : oneLight} language={language} PreTag="div" {...props} data-id="element-50">
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter> : <code className={className} {...props} data-id="element-51">
                          {children}
                        </code>;
                }
              }} data-id="element-48">
                  {markdown}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </div>

        <div className={`flex items-center justify-end gap-4 px-8 py-6 ${isDark ? 'bg-[#2d3340]' : 'bg-[#fafafa]'}`} data-id="element-52">
          <button onClick={onClose} className={`px-6 py-3 rounded-full transition-all duration-200 font-light ${isDark ? 'text-[#7b92a8] hover:bg-[#242830]' : 'text-[#5b7c8d] hover:bg-white'}`} data-id="element-53">
            {t('export.cancel')}
          </button>
          <button onClick={handleExport} disabled={isExporting} className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 font-light ${isDark ? 'bg-[#7b92a8] text-white hover:bg-[#6a8199]' : 'bg-[#5b7c8d] text-white hover:bg-[#4a6b7c]'}`} data-id="element-54">
            <DownloadIcon className="w-4 h-4" data-id="element-55" />
            {isExporting ? t('export.exporting') : t('export.download')}
          </button>
        </div>
      </div>
    </div>;
}